// Align sheet to model (owner, 2026-09-18): the model on the LEFT, the sheet
// on the RIGHT, side by side like Find in Sheets & Model. "Building plan"
// (?ref=building, root-04) aligns the sheet to the overview's plan of a
// level in building feet instead (BuildingSide) - for root-04's pipeline. Pick a grid
// intersection on the sheet and the same one in the model (point A), then a
// second far away (point B); an optional third (C) is only a check. Save
// places the sheet (back/routes/sheetAlignments): two point pairs fix scale,
// turn and position exactly, and every tracking page of that sheet follows.
//
// /align/:projectId?docId=<Doc Control sheet>&page=<n>[&ref=building]
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Button, Segmented, Select, Slider, Space, Tag, Tooltip, message } from "antd";
import WebGlApp from "webgl";
import { url, tokenRef } from "api";
import { getDocByID } from "api/docControl/getDocByID";
import { getDocControl } from "api/docControl/getDocControl";
import useSheetImage from "pages/Tracking/useSheetImage";
import { alignFromPoints } from "pages/Tracking/twoPointAlign";
import PageContainer from "components/PageContainer/PageContainer";
import BuildingSide from "./BuildingSide";
import { sheetKind, sheetRenderable, sheetSelectProps } from "pages/Tracking/sheetOptions";
import ViewerEffectsButton from "components/Viewer/ViewerEffectsButton";
import ViewerViewButtons from "components/Viewer/ViewerViewButtons";
import ViewerToolbar from "components/Viewer/ViewerToolbar";
import { attachBuildingGrid } from "helpers/buildingGridLoad";

const LABELS = ["A", "B", "C"];
const COLORS = ["#FE6919", "#1976d2", "#2e7d32"];
const T = { ink: "#23343E", muted: "#8D8D8D", line: "#EFEFEF", wash: "#F3F7F9" };
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
const fmt = (n, d = 1) => (Number.isFinite(n) ? n.toFixed(d) : "-");

function ModelSide({ projectId, onPick, pickLabel }) {
  const box = useRef(null);
  const app = useRef(null);
  const pickRef = useRef(onPick);
  pickRef.current = onPick;
  useEffect(() => {
    if (!projectId || !box.current) return undefined;
    const noop = () => {};
    app.current = new WebGlApp(
      box.current,
      { glbs: [], manifestUrl: `${url}/uploads/${projectId}/manifest.json.gz` },
      {
        setClickedChild: noop, setLoading: noop, setError: noop, setPercentsLoaded: noop, setAllowToResize: noop, setModal: noop,
        setCoordsForPopover: noop, findInJsonUsingViewer: noop, setIsPopOver: noop, onHoverMesh: noop, selectedMesh: { current: null },
        project: { _id: projectId },
      },
      null, false, false
    );
    const v = app.current.viewer;
    attachBuildingGrid(v, projectId);
    if (v) v.pickPointHandler = (pt) => { if (pickRef.current) pickRef.current({ x: pt.x, y: pt.y, z: pt.z }); };
    return () => { try { if (app.current) app.current.dispose(); } catch (e) { /* best effort */ } app.current = null; };
  }, [projectId]);
  return (
    <div style={{ position: "relative", height: "100%", border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden" }}>
      <div ref={box} style={{ position: "absolute", inset: 0 }} />
      {/* TOP RIGHT, the same corner as every other page. The box
          below is top-LEFT and holds the MODEL tag and the pick
          prompt - the strip does not belong in it. */}
      <div style={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}>
        <ViewerViewButtons viewer={() => app.current && app.current.viewer} variant="floating" />
      </div>
      {/* THE SHARED TOOLBAR, against the STAGE. It positions itself
          absolutely, so inside the top-right box above it would anchor to
          THAT box rather than to this container - the box is the nearest
          positioned ancestor - and land wherever a zero-width corner puts
          it. The model view has it as a direct child of the stage and it
          overlaps nothing there. gear={false}: this page has its own
          ViewerEffectsButton in the row below. */}
{/* A CORNER OF ITS OWN. `viewer-zoom` only carries coordinates in
          the model-view and timeline stylesheets, so on this page the
          class is inert and the toolbar would sit wherever it fell in
          flow (Mark measured x=-41 on tracking, lying across twenty
          controls). A positioned wrapper gives it the same bottom-left
          corner the model view has. */}
      <div style={{ position: "absolute", left: 12, right: 12, bottom: 12, zIndex: 5,
                   display: "flex", justifyContent: "flex-end", pointerEvents: "none" }}>
        <ViewerToolbar viewer={() => app.current && app.current.viewer} gear={false} />
      </div>
      <div style={{ position: "absolute", top: 8, left: 8, display: "flex", gap: 6, zIndex: 2 }}>
        <Tag color="#23343E">MODEL</Tag>
        <ViewerEffectsButton viewer={() => app.current && app.current.viewer} variant="floating" style={{ width: 24, height: 24, borderRadius: 4 }} />
        {pickLabel && <Tag color="orange">Click point {pickLabel} in the model</Tag>}
      </div>
    </div>
  );
}

function SheetSide({ src, points, onPick, pickLabel, onAspect }) {
  const [zoom, setZoom] = useState(1);
  const img = useRef(null);
  const click = (e) => {
    const r = img.current.getBoundingClientRect();
    onPick({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 8px", borderBottom: `1px solid ${T.line}` }}>
        <Tag color="#23343E">SHEET</Tag>
        {pickLabel && <Tag color="orange">Click point {pickLabel} on the sheet</Tag>}
        <span style={{ marginLeft: "auto", fontSize: 12, color: T.muted }}>Zoom</span>
        <Slider style={{ width: 160 }} min={1} max={8} step={0.25} value={zoom} onChange={setZoom} />
      </div>
      <div style={{ flex: 1, overflow: "auto", background: T.wash }}>
        {src ? (
          <div style={{ position: "relative", width: `${zoom * 100}%`, cursor: "crosshair" }} onClick={click}>
            <img ref={img} src={src} alt="" draggable={false} style={{ width: "100%", display: "block", userSelect: "none" }}
              onLoad={(e) => onAspect(e.target.naturalWidth / e.target.naturalHeight)} />
            {points.map((p, i) => p && (
              <div key={i} style={{ position: "absolute", left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%,-50%)", pointerEvents: "none" }}>
                <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${COLORS[i]}`, background: "rgba(255,255,255,.35)" }} />
                <div style={{ position: "absolute", left: 20, top: -4, fontWeight: 700, color: COLORS[i], textShadow: "0 0 3px #fff" }}>{LABELS[i]}</div>
              </div>
            ))}
          </div>
        ) : <div style={{ padding: 24, color: T.muted }}>Loading the sheet…</div>}
      </div>
    </div>
  );
}

export default function AlignSheetPage() {
  const { projectId } = useParams();
  const loc = useLocation();
  const history = useHistory();
  const qs = useMemo(() => new URLSearchParams(loc.search), [loc.search]);
  const docId = qs.get("docId") || "";
  const page = Number(qs.get("page")) || 1;
  const kind = qs.get("ref") === "building" ? "building" : "model";
  const [manifest, setManifest] = useState(null);
  const [levelId, setLevelId] = useState("");
  const [doc, setDoc] = useState(null);
  const [docs, setDocs] = useState([]);
  const [pairs, setPairs] = useState([{}, {}, {}]);   // { sheet, ref } per A, B, C
  const [active, setActive] = useState(0);
  const [aspect, setAspect] = useState(0);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const src = useSheetImage(doc, { scale: 3, maxWidth: 4096, quality: 0.92, page });

  useEffect(() => {
    getDocControl({ projectId, limit: 500 }).then((r) => {
      const list = Array.isArray(r) ? r : (r && (r.docs || r.data || r.items)) || [];
      // Drawable documents only (sheetOptions): PDFs, images, rendered DWGs.
      setDocs(list.filter((d) => sheetRenderable(d) || sheetKind(d) === "dwg"));
    }).catch(() => setDocs([]));
  }, [projectId]);
  useEffect(() => {
    setDoc(null); setPairs([{}, {}, {}]); setActive(0); setSaved(null);
    if (!docId) return;
    getDocByID(docId).then((r) => setDoc(r && (r.doc || r.data || r))).catch(() => setDoc(null));
    fetch(`${url}/projects/${projectId}/sheet-alignments?docId=${docId}&page=${page}&kind=${kind}`, { headers: auth() })
      .then((r) => r.json()).then((b) => {
        if (!b || !b.alignment) return;
        setSaved(b.alignment);
        if (b.alignment.reference && b.alignment.reference.levelId) setLevelId(b.alignment.reference.levelId);
      }).catch(() => {});
  }, [projectId, docId, page, kind]);
  useEffect(() => {
    if (kind !== "building" || manifest) return;
    fetch(`${url}/projects/${projectId}/glance`, { headers: auth() }).then((r) => r.json())
      .then((b) => setManifest((b && b.manifest) || { levels: [] })).catch(() => setManifest({ levels: [] }));
  }, [kind, projectId, manifest]);
  // The sheet's level by name (LEVEL 1 / L1 / First floor -> the overview's L1), else the first.
  useEffect(() => {
    if (kind !== "building" || !manifest || levelId) return;
    const levels = manifest.levels || [];
    const name = String((doc && (doc.sheetName || doc.name)) || "");
    const m = /\b(?:level|lvl|l)\s*0*(\d{1,2})\b/i.exec(name);
    const hit = m && levels.find((l) => new RegExp(`^(?:level|lvl|l)\\s*0*${m[1]}$`, "i").test(String(l.name || l.id).trim()) || String(l.id).toUpperCase() === `L${m[1]}`);
    if (hit || levels[0]) setLevelId((hit || levels[0]).id);
  }, [kind, manifest, doc, levelId]);

  // A pair is complete with both points; the next click fills the first incomplete one.
  const setPoint = useCallback((side, p) => {
    setPairs((prev) => {
      const next = prev.map((x) => ({ ...x }));
      next[active] = { ...next[active], [side]: p };
      return next;
    });
  }, [active]);
  useEffect(() => {
    const cur = pairs[active];
    if (cur && cur.sheet && cur.ref && active < 2) setActive(active + 1);
  }, [pairs, active]);

  const complete = pairs.filter((p) => p.sheet && p.ref);
  // A building point (x, y north up) is the model's (x, z = -y) to the shared arithmetic.
  const refFrame = (r) => (kind === "building" ? { x: r.x, z: -r.y } : r);
  const result = complete.length >= 2 && aspect ? alignFromPoints(complete.map((p) => p.sheet), complete.map((p) => refFrame(p.ref)), aspect) : null;
  const pickLabel = LABELS[active];

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${url}/projects/${projectId}/sheet-alignments`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ docId, page, aspect, kind, levelId, points: complete }),
      });
      const b = await res.json();
      if (!res.ok) throw new Error(b.err || "Could not save");
      setSaved(b.alignment);
      message.success(`Sheet placed${b.trackingPagesPlaced ? ` - ${b.trackingPagesPlaced} tracking page(s) follow it` : ""}.`);
    } catch (e) { message.error(e.message); } finally { setSaving(false); }
  };

  return (
    <PageContainer wide style={{ height: "calc(100 * var(--vh) - 110px)", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: T.ink }}>Align sheet to {kind === "building" ? "building plan" : "model"}</div>
        <Segmented size="small" value={kind} options={[{ label: "Model", value: "model" }, { label: "Building plan", value: "building" }]}
          onChange={(v) => { qs.set("ref", v); if (v === "model") qs.delete("ref"); history.replace(`/align/${projectId}?${qs.toString()}`); }} />
        <Select style={{ minWidth: 380 }} placeholder="Choose a sheet" value={docId || undefined}
          {...sheetSelectProps([...docs, ...(doc && doc._id && !docs.some((d) => String(d._id) === String(doc._id)) ? [doc] : [])],
            { labelOf: (d) => [d.sheetNumber, d.sheetName || d.name].filter(Boolean).join(" — ") || String(d._id) })}
          onChange={(v) => history.replace(`/align/${projectId}?docId=${v}&page=1${kind === "building" ? "&ref=building" : ""}`)} />
        {saved && <Tag color="green">Placed {new Date(saved.updatedAt).toLocaleString()}{saved.byName ? ` by ${saved.byName}` : ""}</Tag>}
      </div>
      <div style={{ fontSize: 12.5, color: T.muted }}>
        Pick the same grid intersection on the sheet (right) and {kind === "building" ? "on the building plan" : "in the model"} (left) for A, then a second one far away for B. C is optional: it only checks the result.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr) 250px", gap: 10, flex: 1, minHeight: 0 }}>
        {kind === "building"
          ? <BuildingSide manifest={manifest} levelId={levelId} onLevel={(v) => { setLevelId(v); setPairs((prev) => prev.map((x) => ({ sheet: x.sheet }))); }}
              points={pairs.map((p) => p.ref)} colors={COLORS} labels={LABELS} onPick={(p) => setPoint("ref", p)} pickLabel={pickLabel} />
          : <ModelSide projectId={projectId} onPick={(p) => setPoint("ref", p)} pickLabel={pickLabel} />}
        <SheetSide src={src} points={pairs.map((p) => p.sheet)} onPick={(p) => setPoint("sheet", p)} pickLabel={pickLabel} onAspect={setAspect} />
        <div style={{ border: `1px solid ${T.line}`, borderRadius: 6, padding: 10, overflow: "auto" }}>
          {pairs.map((p, i) => (
            <div key={i} onClick={() => setActive(i)} style={{ padding: 8, marginBottom: 6, borderRadius: 6, cursor: "pointer", border: `1px solid ${active === i ? COLORS[i] : T.line}` }}>
              <div style={{ fontWeight: 700, color: COLORS[i] }}>{LABELS[i]}{i === 2 ? " (check, optional)" : ""}</div>
              <div style={{ fontSize: 12 }}>Sheet: {p.sheet ? `${fmt(p.sheet.x)}%, ${fmt(p.sheet.y)}%` : "-"}</div>
              <div style={{ fontSize: 12 }}>{kind === "building"
                ? <>Plan: {p.ref ? `${p.ref.label ? `grid ${p.ref.label} - ` : ""}x ${fmt(p.ref.x)}, y ${fmt(p.ref.y)} ft` : "-"}</>
                : <>Model: {p.ref ? `x ${fmt(p.ref.x)}, z ${fmt(p.ref.z)}` : "-"}</>}</div>
              {(p.sheet || p.ref) && <Button size="small" type="link" style={{ padding: 0 }} onClick={(e) => { e.stopPropagation(); setPairs((prev) => prev.map((x, k) => (k === i ? {} : x))); setActive(i); }}>Redo</Button>}
            </div>
          ))}
          {result && (
            <div style={{ fontSize: 12.5, margin: "8px 0", color: T.ink }}>
              Sheet width {fmt(result.scale)} ft, turned {fmt(result.rotation, 2)}°
              {result.residual != null && <div style={{ color: result.residual > 3 ? "#c62828" : "#2e7d32" }}>Check point C is {fmt(result.residual)} ft off</div>}
              {result.mirrored && <div role="alert" style={{ color: "#c62828", fontWeight: 700 }}>These points describe a mirror image of the sheet (C is {fmt(result.mirrorResidual)} ft off mirrored). A sheet never needs mirroring: pick A and B in the same order on both sides, on the same grid intersections. It cannot be saved like this.</div>}
              {result.residual == null && <div className="g-tsub">Add a check point C to confirm the placement - it also catches points picked mirror-wise.</div>}
            </div>
          )}
          <Space direction="vertical" style={{ width: "100%" }}>
            <Tooltip title={!docId ? "Choose a sheet first" : complete.length < 2 ? "Pick points A and B on both sides" : ""}>
              <Button type="primary" block disabled={!docId || !result || !aspect || result.mirrored} loading={saving} onClick={save}>Save placement</Button>
            </Tooltip>
            <Button block onClick={() => history.push(kind === "building" ? `/coordination/${projectId}` : `/tracking/${projectId}`)}>{kind === "building" ? "Back to the overview" : "Back to tracking"}</Button>
          </Space>
        </div>
      </div>
    </PageContainer>
  );
}
